import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { useOrg } from '../../../contexts/OrgContext'

const REVIEW_CYCLE_OPTIONS = ['Annual', 'Semi-Annual', 'Quarterly', 'Biennial']

const TONE_OPTIONS = [
  { label: 'Formal & Regulatory', value: 'Formal & regulatory' },
  { label: 'Professional', value: 'Professional' },
  { label: 'Accessible', value: 'Accessible' },
  { label: 'Plain Language', value: 'Plain language' }
]

const REQUIREMENT_VERB_OPTIONS = ['shall', 'must', 'will']
const RECOMMENDATION_VERB_OPTIONS = ['should', 'may', 'is recommended']

const DEFAULT_ROLE_TITLES = {
  security_lead: 'Information Security Officer',
  executive_sponsor: 'Chief Executive Officer',
  it_lead: 'Director of Information Technology',
  hr_lead: 'Director of Human Resources',
  compliance_lead: 'Compliance Officer'
}

const ROLE_LABELS = {
  security_lead: 'Security Lead',
  executive_sponsor: 'Executive Sponsor',
  it_lead: 'IT Lead',
  hr_lead: 'HR Lead',
  compliance_lead: 'Compliance Lead'
}

const FONT_OPTIONS = ['Calibri', 'Georgia']

function PillToggle({ options, value, onChange, label }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>}
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const optValue = typeof opt === 'string' ? opt : opt.value
          const optLabel = typeof opt === 'string' ? opt : opt.label
          const isSelected = value === optValue
          return (
            <button
              key={optValue}
              type="button"
              onClick={() => onChange(optValue)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isSelected
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
              }`}
            >
              {optLabel}
            </button>
          )
        })}
      </div>
    </div>
  )
}


function PolicySettingsPanel({ onClose }) {
  const { user } = useAuth()
  const { currentOrg: org } = useOrg() || {}
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [activeTab, setActiveTab] = useState('generation')
  const logoInputRef = useRef(null)

  // Generation Settings state
  const [firmName, setFirmName] = useState('')
  const [openingStyle, setOpeningStyle] = useState('')
  const [standardDisclaimer, setStandardDisclaimer] = useState('')
  const [defaultReviewCycle, setDefaultReviewCycle] = useState('Annual')
  const [requirementVerb, setRequirementVerb] = useState('shall')
  const [recommendationVerb, setRecommendationVerb] = useState('should')
  const [tone, setTone] = useState('Professional')
  const [roleTitles, setRoleTitles] = useState({ ...DEFAULT_ROLE_TITLES })
  // Brand Config state
  const [logoUrl, setLogoUrl] = useState('')
  const [logoUploading, setLogoUploading] = useState(false)
  const [primaryColor, setPrimaryColor] = useState('#0D1B2A')
  const [accentColor, setAccentColor] = useState('#C9A84C')
  const [fontPreference, setFontPreference] = useState('Calibri')
  const [firmDisplayName, setFirmDisplayName] = useState('')
  const [trademarkLine, setTrademarkLine] = useState('')
  const [colorDetected, setColorDetected] = useState(false)

  useEffect(() => {
    if (!user) return
    const fetchAll = async () => {
      setLoading(true)

      // Fetch policy_settings
      const { data } = await supabase
        .from('policy_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (data) {
        setFirmName(data.firm_name || '')
        setOpeningStyle(data.style_guide || '')
        setStandardDisclaimer(data.standard_disclaimer || '')
        setDefaultReviewCycle(data.default_review_cycle || 'Annual')
        setRequirementVerb(data.language_preferences?.requirement_verb || 'shall')
        setRecommendationVerb(data.language_preferences?.recommendation_verb || 'should')
        setTone(data.language_preferences?.tone || 'Professional')
        setRoleTitles({ ...DEFAULT_ROLE_TITLES, ...(data.preferred_role_titles || {}) })
      }

      // Fetch brand config
      const orgId = org?.id
      if (orgId) {
        const { data: brand } = await supabase
          .from('org_brand_config')
          .select('*')
          .eq('org_id', orgId)
          .maybeSingle()

        if (brand) {
          setLogoUrl(brand.logo_url || '')
          setPrimaryColor(brand.primary_color || '#0D1B2A')
          setAccentColor(brand.accent_color || '#C9A84C')
          setFontPreference(brand.font_preference || 'Calibri')
          setFirmDisplayName(brand.firm_display_name || '')
          setTrademarkLine(brand.trademark_line || '')
        }
      }

      setLoading(false)
    }
    fetchAll()
  }, [user, org?.id])

  useEffect(() => {
    if (saveSuccess) {
      const t = setTimeout(() => setSaveSuccess(false), 3000)
      return () => clearTimeout(t)
    }
  }, [saveSuccess])

  const extractDominantColor = (file) => {
    const img = new Image()
    const reader = new FileReader()
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = 10
        canvas.height = 10
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, 10, 10)
        const data = ctx.getImageData(0, 0, 10, 10).data
        let r = 0, g = 0, b = 0, count = 0
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] > 128) { // only non-transparent pixels
            r += data[i]; g += data[i + 1]; b += data[i + 2]; count++
          }
        }
        if (count > 0) {
          r = Math.round(r / count); g = Math.round(g / count); b = Math.round(b / count)
          const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
          setPrimaryColor(hex)
          setColorDetected(true)
        }
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      setSaveError('Logo must be under 2MB')
      return
    }

    setLogoUploading(true)
    setSaveError('')

    // Extract color
    extractDominantColor(file)

    const orgId = org?.id || 'default'
    const ext = file.name.split('.').pop()
    const path = `${orgId}/policy-logo.${ext}`

    try {
      const { error: uploadError } = await supabase.storage
        .from('org-assets')
        .upload(path, file, { upsert: true })

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage.from('org-assets').getPublicUrl(path)
      setLogoUrl(publicUrl)
    } catch (err) {
      setSaveError('Logo upload failed: ' + (err.message || 'Unknown error'))
    } finally {
      setLogoUploading(false)
    }
  }

  const removeLogo = () => {
    setLogoUrl('')
    setColorDetected(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError('')
    setSaveSuccess(false)

    try {
      // Save policy_settings
      const payload = {
        user_id: user.id,
        firm_name: firmName,
        style_guide: openingStyle,
        standard_disclaimer: standardDisclaimer,
        default_review_cycle: defaultReviewCycle,
        language_preferences: {
          requirement_verb: requirementVerb,
          recommendation_verb: recommendationVerb,
          tone
        },
        preferred_role_titles: roleTitles
      }

      const { error: settingsErr } = await supabase
        .from('policy_settings')
        .upsert(payload, { onConflict: 'user_id' })

      if (settingsErr) throw settingsErr

      // Save brand config
      const orgId = org?.id
      if (orgId) {
        const { error: brandErr } = await supabase
          .from('org_brand_config')
          .upsert({
            org_id: orgId,
            logo_url: logoUrl,
            primary_color: primaryColor,
            accent_color: accentColor,
            font_preference: fontPreference,
            firm_display_name: firmDisplayName,
            trademark_line: trademarkLine
          }, { onConflict: 'org_id' })

        if (brandErr) throw brandErr
      }

      setSaveSuccess(true)
    } catch (err) {
      console.error('Error saving policy settings:', err)
      setSaveError(err.message || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const updateRoleTitle = (key, value) => {
    setRoleTitles(prev => ({ ...prev, [key]: value }))
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Policy Generation Settings</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Configure how AI generates policy documents.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('generation')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'generation'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Generation Settings
            </button>
            <button
              onClick={() => setActiveTab('branding')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'branding'
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
              </svg>
              Document Branding
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {saveError && (
            <div className="p-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {saveError}
            </div>
          )}

          {/* ── Generation Settings Tab ── */}
          {activeTab === 'generation' && (
            <div className="space-y-6">
              {/* Review Cycle — moved to top */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Review Cycle</label>
                <select
                  value={defaultReviewCycle}
                  onChange={e => setDefaultReviewCycle(e.target.value)}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {REVIEW_CYCLE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Advisory Firm Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Advisory Firm Name</label>
                <input
                  type="text"
                  value={firmName}
                  onChange={e => setFirmName(e.target.value)}
                  placeholder="e.g., Sidewalk Security Advisors"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Used as the default authoring entity in generated policies</p>
              </div>

              {/* Writing Tone — pill toggle */}
              <PillToggle
                label="Writing Tone"
                options={TONE_OPTIONS}
                value={tone}
                onChange={setTone}
              />

              {/* Requirement Verb — pill toggle */}
              <PillToggle
                label="Requirement Verb"
                options={REQUIREMENT_VERB_OPTIONS}
                value={requirementVerb}
                onChange={setRequirementVerb}
              />

              {/* Recommendation Verb — pill toggle */}
              <PillToggle
                label="Recommendation Verb"
                options={RECOMMENDATION_VERB_OPTIONS}
                value={recommendationVerb}
                onChange={setRecommendationVerb}
              />

              {/* Opening Style — replaces old style_guide textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Standard Policy Opening Structure</label>
                <textarea
                  value={openingStyle}
                  onChange={e => setOpeningStyle(e.target.value)}
                  placeholder="Describe your preferred way to open a policy purpose statement."
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Standard Disclaimer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Standard Policy Disclaimer</label>
                <textarea
                  value={standardDisclaimer}
                  onChange={e => setStandardDisclaimer(e.target.value)}
                  placeholder="This policy is the property of [ORGANIZATION NAME]. Unauthorized distribution is prohibited."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Role Titles */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-3">Preferred Role Titles</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  The AI will use these role titles instead of generic ones.
                </p>
                <div className="space-y-3">
                  {Object.entries(ROLE_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-center gap-3">
                      <label className="text-sm text-gray-600 dark:text-gray-400 w-40 flex-shrink-0">{label}</label>
                      <input
                        type="text"
                        value={roleTitles[key] || ''}
                        onChange={e => updateRoleTitle(key, e.target.value)}
                        placeholder={DEFAULT_ROLE_TITLES[key]}
                        className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Document Branding Tab ── */}
          {activeTab === 'branding' && (
            <div className="flex gap-8">
              <div className="flex-1 space-y-6">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization Logo</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Appears in the header of exported policy documents. PNG, JPG, or SVG. Max 2MB.</p>
                  {logoUrl ? (
                    <div>
                      <div className="flex items-center justify-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 mb-2">
                        <img src={logoUrl} alt="Logo" className="max-w-[200px] max-h-[80px] object-contain" />
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => logoInputRef.current?.click()} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">Change logo</button>
                        <button onClick={removeLogo} className="text-xs text-red-600 dark:text-red-400 hover:underline">Remove</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
                    >
                      <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                      </svg>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{logoUploading ? 'Uploading...' : 'Click to upload'}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PNG, JPG, SVG up to 2MB</p>
                    </button>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  {colorDetected && (
                    <div className="mt-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg flex items-center gap-3">
                      <div className="w-8 h-8 rounded flex-shrink-0 border border-gray-200 dark:border-gray-600" style={{ backgroundColor: primaryColor }} />
                      <div className="flex-1">
                        <p className="text-xs text-gray-700 dark:text-gray-300">Color detected from your logo</p>
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{primaryColor}</p>
                      </div>
                      <button onClick={() => setColorDetected(false)} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">Dismiss</button>
                    </div>
                  )}
                </div>

                {/* Colors — side by side */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Document Colors</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Header color</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={e => { setPrimaryColor(e.target.value); setColorDetected(false) }}
                          className="w-10 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                        />
                        <input
                          type="text"
                          value={primaryColor}
                          onChange={e => { setPrimaryColor(e.target.value); setColorDetected(false) }}
                          className="flex-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Headers and section headings</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Accent color</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={accentColor}
                          onChange={e => setAccentColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                        />
                        <input
                          type="text"
                          value={accentColor}
                          onChange={e => setAccentColor(e.target.value)}
                          className="flex-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Dividers and highlights</p>
                    </div>
                  </div>
                </div>

                {/* Font Preference */}
                <PillToggle
                  label="Document Font"
                  options={FONT_OPTIONS}
                  value={fontPreference}
                  onChange={setFontPreference}
                />

                {/* Firm Display Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document Header Name</label>
                  <input
                    type="text"
                    value={firmDisplayName}
                    onChange={e => setFirmDisplayName(e.target.value)}
                    placeholder="Defaults to your organization name if left blank"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Document Footer / Trademark Line */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Document Footer Line</label>
                  <input
                    type="text"
                    value={trademarkLine}
                    onChange={e => setTrademarkLine(e.target.value)}
                    placeholder="e.g., Prepared by Acme Advisory Group"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Note: The SSA trademark line always appears in document footers and cannot be removed.</p>
                </div>
              </div>

              {/* Live Cover Preview */}
              <div className="flex-shrink-0">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Cover Preview</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Updates as you adjust settings.</p>
                <div
                  className="w-[280px] rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm"
                  style={{ fontFamily: fontPreference === 'Georgia' ? 'Georgia, serif' : 'Calibri, sans-serif' }}
                >
                  <div className="px-5 py-6" style={{ backgroundColor: primaryColor }}>
                    <p className="text-white text-xs font-bold tracking-wider uppercase truncate">{firmDisplayName || firmName || 'Your Firm Name'}</p>
                    <p className="text-white/60 text-[10px] mt-1">Information Security Advisory</p>
                  </div>
                  <div className="h-[3px]" style={{ backgroundColor: accentColor }} />
                  <div className="bg-white dark:bg-gray-800 px-5 py-6">
                    <p className="text-lg font-bold mb-1" style={{ color: primaryColor }}>Access Control Policy</p>
                    <p className="text-xs text-gray-400 mb-1">SSA-POL-AC-001 &middot; v1.0</p>
                    <p className="text-xs text-gray-400 mb-4">Prepared for: Sample Organization</p>
                    <div className="rounded-lg p-2 mb-3 grid grid-cols-2 gap-1" style={{ backgroundColor: primaryColor + '15' }}>
                      <div><p className="text-[9px] text-gray-400">Effective</p><p className="text-[10px] font-medium text-gray-700 dark:text-gray-300">[Date]</p></div>
                      <div><p className="text-[9px] text-gray-400">Review</p><p className="text-[10px] font-medium text-gray-700 dark:text-gray-300">Annual</p></div>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {['NIST CSF', 'ISO 27001', 'SOC 2'].map(f => (
                        <span key={f} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">{f}</span>
                      ))}
                    </div>
                  </div>
                  {trademarkLine && (
                    <div className="px-5 py-2 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-[9px] text-gray-400 truncate">{trademarkLine}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            {saveSuccess && (
              <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Settings saved
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PolicySettingsPanel
